import { useEffect, useState } from 'react';
import clsx from 'clsx';
import './RollingNumber.css';

/* ------------------------------------------------------------------ *
 * The counter drum.
 *
 * Each digit is a vertical drum of 0–9 behind a one-line window,
 * rolled with the spring easing — so a metric doesn't change, it
 * *turns over*, the way a scale's mechanical counter does. On mount
 * every drum rolls up from 0, which is the dashboard's opening move.
 *
 * Layout is exact by construction: a hidden copy of the target digit
 * provides the width and the baseline, so the component aligns with
 * neighbouring text at any font size and never shifts layout. The
 * spring's overshoot briefly shows a sliver of the neighbouring
 * numeral — that is not a bug, that is the drum.
 *
 * Accepts any formatted string; only the digits roll. Under
 * prefers-reduced-motion the global kill-switch makes every roll
 * instant.
 * ------------------------------------------------------------------ */

const DRUM = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

export function RollingNumber({
  value,
  className,
}: {
  value: string | number;
  className?: string | undefined;
}) {
  const text = String(value);
  return (
    <span className={clsx('pd-roll', className)}>
      <span className="sr-only">{text}</span>
      <span className="pd-roll__chars" aria-hidden="true">
        {[...text].map((ch, i) =>
          /[0-9]/.test(ch) ? (
            <RollDigit key={`d${i}`} target={Number(ch)} delayMs={i * 26} />
          ) : (
            <span key={`c${i}`}>{ch}</span>
          ),
        )}
      </span>
    </span>
  );
}

function RollDigit({ target, delayMs }: { target: number; delayMs: number }) {
  // Starts at 0 and rolls to the target one frame later — the
  // entrance. Subsequent prop changes roll from wherever it stands.
  const [shown, setShown] = useState(0);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(target));
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return (
    <span className="pd-roll__digit">
      {/* Invisible target digit: defines width and baseline. */}
      <span className="pd-roll__ph">{target}</span>
      <span className="pd-roll__win">
        <span
          className="pd-roll__col"
          style={{
            translate: `0 calc(${shown} * -1lh)`,
            transitionDelay: `${delayMs}ms`,
          }}
        >
          {DRUM.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </span>
      </span>
    </span>
  );
}
